import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { from, Observable } from 'rxjs';
import { filter, map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { ArrayHelper } from '../../helpers/arrayHelper';
import { ComponentBase } from '../../helpers/ComponentBase';
import { StringHelper } from '../../helpers/stringHelper';
import { Entity } from '../../model/entities/Entity';
import { IIndexedArray } from '../../model/IIndexedArray';
import { ILinkedItemsListParams } from '../../model/linkedItemsList/ILinkedItemsListParams';
import { IStoreDocument } from '../../model/store/IStoreDocument';
import { PatternsHelper } from '../../modules/utils/helpers/patterns.helper';
import { EntityBuilder } from '../../services/EntityBuilder';
import { EntityLinkService } from '../../services/entityLink.service';
import { ShowMessageParamsPopup } from '../../services/interfaces/ShowMessageParamsPopup';
import { PatternResolverService } from '../../services/pattern-resolver.service';
import { UiMessageService } from '../../services/uiMessage.service';

@Component({
	templateUrl: './linkedItemsList.component.html',
	styleUrls: ['./linkedItemsList.component.scss'],
})
export class LinkedItemsListComponent<T extends IStoreDocument> extends ComponentBase implements OnInit {

	//#region FIELDS

	private static readonly C_LOG_ID = "LIL.C::";

	//#endregion

	//#region PROPERTIES

	/** Liens indexés par catégorie. */
	public linksByCategory: IIndexedArray<Array<Entity<T>>>;

	private mbHasElements: boolean;
	public get hasElements(): boolean { return this.mbHasElements }

	/** Paramètres du composant. */
	@Input() public params: ILinkedItemsListParams;

	//#endregion

	//#region METHODS

	constructor(
		private readonly isvcEntityLink: EntityLinkService,
		private readonly isvcUiMessage: UiMessageService,
		private readonly ioModalCtrl: ModalController,
		private readonly isvcPatternResolver: PatternResolverService,
		private readonly ioRouter: Router,
		poChangeDetectorRef: ChangeDetectorRef
	) {
		super(poChangeDetectorRef);
	}

	public ngOnInit(): void {
		if (!this.params)
			this.params = {};

		if (StringHelper.isBlank(this.params.itemId))
			this.params.itemId = "";
		else if (typeof this.params.itemId === "string" && PatternsHelper.hasPattern(this.params.itemId))
			this.params.itemId = this.isvcPatternResolver.replaceDynParams(this.params.itemId, this.params.itemId);

		this.init().subscribe();
	}

	private init(): Observable<IIndexedArray<Entity<T>[]>> {
		return this.fillItems()
			.pipe(
				tap(
					(poEntitiesByCategory: IIndexedArray<Entity<T>[]>) => {
						console.debug(`${LinkedItemsListComponent.C_LOG_ID}Entity links successfully indexed:`, poEntitiesByCategory);
						this.detectChanges();
					},
					poError => {
						console.error(`${LinkedItemsListComponent.C_LOG_ID}Failed to fill linked entities list.`, poError);

						this.isvcUiMessage.showMessage(
							new ShowMessageParamsPopup({
								header: "Erreur",
								message: "Erreur lors de la récupération des informations liées.",
								buttons: [{ text: "OK" }]
							})
						);
					}
				),
				takeUntil(this.destroyed$)
			);
	}

	/** Remplis la liste des items en les indexant par préfixe. */
	private fillItems(): Observable<IIndexedArray<Entity<T>[]>> {
		return this.isvcEntityLink.getLinkedEntities(this.params.itemId, this.params.linkedEntityPrefixes, true)
			.pipe(
				filter((paEntities: T[]) => this.mbHasElements = ArrayHelper.hasElements(paEntities)),
				map((paEntities: T[]) => this.indexLinks(paEntities))
			);
	}

	/** Permet d'indexer les items par leur préfixe. */
	private indexLinks(paLinks: T[]): IIndexedArray<Entity<T>[]> {
		this.linksByCategory = {};

		paLinks.forEach((poItem: T) => {
			try {
				const loEntityBuilder: EntityBuilder<T> = this.isvcEntityLink.getEntityBuilder(poItem._id);
				const lsCategory: string = loEntityBuilder.category(poItem._id);

				if (!this.linksByCategory[lsCategory])
					this.linksByCategory[lsCategory] = [];

				this.linksByCategory[lsCategory].push(loEntityBuilder.build(poItem));
			}
			catch (poError) {
				// En cas d'erreur, on n'empêche pas l'affichage des entités liées.
				console.error(`${LinkedItemsListComponent.C_LOG_ID}Cannot display entity ${poItem._id}.`, poError);
			}
		});

		return this.linksByCategory;
	}

	/** Lance la navigation vers un item.
	 * @param poItem Item vers lequel naviguer.
	 */
	public goToItem(poItem: Entity<T>): void {
		if (!StringHelper.isBlank(poItem.route)) {
			from(this.ioRouter.navigateByUrl(poItem.route))
				.pipe(
					mergeMap(_ => this.ioModalCtrl.dismiss()),
					tap(
						_ => { },
						poError => console.error(`${LinkedItemsListComponent.C_LOG_ID}Error to go to item`, poError)
					)
				)
				.subscribe();
		}
	}

	//#endregion

}